/**
 * BulkUpdateTimesModal
 *
 * Collects new call / start / end times to apply to every selected event.
 * Each event keeps its own date — only the wall-clock times change (the
 * parent combines them per event via combineEventDateWithTime). Fields left
 * blank are not touched, so "set crew call to 17:00 everywhere" doesn't
 * clobber start/end times.
 *
 * Submitting hands the chosen times to the parent, which chains the
 * type-the-count confirmation before anything is written.
 */

import { useEffect, useState } from 'react';
import Modal from '../ui/Modal';

export interface BulkTimes {
  callTime?: string;
  startTime?: string;
  endTime?: string;
}

interface BulkUpdateTimesModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with only the fields the user filled in. */
  onSubmit: (times: BulkTimes) => void;
  eventCount: number;
}

const FIELDS: { key: keyof BulkTimes; label: string }[] = [
  { key: 'callTime', label: 'Crew Call' },
  { key: 'startTime', label: 'Start Time' },
  { key: 'endTime', label: 'End Time' },
];

export default function BulkUpdateTimesModal({
  isOpen,
  onClose,
  onSubmit,
  eventCount,
}: BulkUpdateTimesModalProps) {
  const [times, setTimes] = useState<Record<keyof BulkTimes, string>>({
    callTime: '',
    startTime: '',
    endTime: '',
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setTimes({ callTime: '', startTime: '', endTime: '' });
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const filled = Object.fromEntries(
      Object.entries(times).filter(([, value]) => value !== '')
    ) as BulkTimes;
    if (Object.keys(filled).length === 0) {
      setError('Set at least one time');
      return;
    }
    onSubmit(filled);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" labelledBy="bulk-times-title">
      <form onSubmit={handleSubmit}>
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 id="bulk-times-title" className="text-lg font-semibold text-gray-900">
            Update Times for {eventCount} Event{eventCount === 1 ? '' : 's'}
          </h3>
        </div>

        <div className="px-6 py-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
              {error}
            </div>
          )}
          <p className="text-sm text-gray-500">
            Each event keeps its own date; only the times you set below change.
            Leave a field blank to keep that time as-is.
          </p>
          {FIELDS.map(({ key, label }) => (
            <div key={key}>
              <label htmlFor={`bulk-${key}`} className="block text-sm font-medium text-gray-700 mb-1">
                {label}
              </label>
              <input
                id={`bulk-${key}`}
                type="time"
                value={times[key]}
                onChange={(e) => {
                  setTimes((prev) => ({ ...prev, [key]: e.target.value }));
                  setError(null);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-rack focus:border-rack"
              />
            </div>
          ))}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md"
          >
            Continue
          </button>
        </div>
      </form>
    </Modal>
  );
}
